/**
 * Runtime configuration utility
 *
 * This module provides access to environment variables at runtime.
 *
 * In production, variables are injected via window.env
 * In development, falls back to import.meta.env (Vite's build-time vars)
 */

const getEnv = (): { [key: string]: string | undefined } => {
  const windowEnv = (window as any).env;
  const isValidWindowEnv =
    windowEnv && typeof windowEnv === 'object' && !(windowEnv instanceof Element);
  return isValidWindowEnv ? windowEnv : (import.meta as any).env || {};
};

/**
 * Get a specific environment variable
 */
export const getEnvVar = (key: string): string | undefined => {
  return getEnv()[key];
};

/**
 * Get all environment variables
 */
export const getAllEnvVars = (): { [key: string]: string | undefined } => {
  return getEnv();
};

/**
 * Type-safe getters for specific environment variables
 */
export const runtimeConfig = {
  get apiBaseNewUrl(): string | undefined {
    return getEnvVar('VITE_REACT_APP_API_BASE_NEW_URL');
  },

  get mmsApiBaseUrl(): string | undefined {
    return getEnvVar('VITE_REACT_APP_MMS_API_BASE_URL');
  },

  get paylinkApiBaseUrl(): string | undefined {
    return getEnvVar('VITE_REACT_APP_PAYLINK_API_BASE_URL');
  },

  get omsApiBaseUrl(): string | undefined {
    return getEnvVar('VITE_REACT_APP_OMS_API_BASE_URL');
  },

  get auth0Domain(): string | undefined {
    return getEnvVar('VITE_REACT_APP_AUTH0_DOMAIN');
  },

  get auth0ClientId(): string | undefined {
    return getEnvVar('VITE_REACT_APP_AUTH0_CLIENT_ID');
  },

  get auth0Audience(): string | undefined {
    return getEnvVar('VITE_REACT_APP_AUTH0_AUDIENCE');
  },

  get ldClientSideId(): string | undefined {
    return getEnvVar('VITE_REACT_APP_LD_CLIENT_SIDE_ID');
  },

  get webhooksApiBaseUrl(): string | undefined {
    return getEnvVar('VITE_REACT_APP_WEBHOOKS_API_BASE_URL');
  },

  get ellaDataLayerApiBaseUrl(): string | undefined {
    return getEnvVar('VITE_REACT_APP_ELLA_DATA_LAYER_API_BASE_URL');
  },

  get ecomConnectorApiBaseUrl(): string | undefined {
    return getEnvVar('VITE_REACT_APP_ECOM_CONNECTOR_API_BASE_URL');
  },

  /** Ella API origin the storefront SDK talks to. Same value as ecom-connector's ELLA_API_URL. */
  get ellaApiUrl(): string | undefined {
    return getEnvVar('VITE_REACT_APP_ELLA_API_URL');
  },

  /** Ella storefront SDK bundle. Same value as ecom-connector's ELLA_SDK_URL. */
  get ellaSdkUrl(): string | undefined {
    return getEnvVar('VITE_REACT_APP_ELLA_SDK_URL');
  },
};

export default getEnv;
